/**
 * Track 5: conversation with an NPC leader.
 * Two kinds of lines are always visually separate:
 *  - "NPC beszéd": what the leader says (written by a language model or a template, tagged accordingly) — flavour only;
 *  - "Szabálymotor": the mechanical result, written by the game rules. Only this decides what happened.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { NpcConversation, NpcConversationMessage, ProposalType, ProposalView, RelationView } from '@galaxis/shared';
import { NPC_PLAYER_MESSAGE_MAX } from '@galaxis/shared';
import { fetchNpcConversation, talkToNpc, type NpcTalkBody } from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';

const ARCHETYPE_LABEL = { expansionist: 'Terjeszkedő', industrial: 'Iparos', militarist: 'Militarista', peaceful: 'Békés' } as const;
const ATTITUDE_LABEL = { hostile: 'ellenséges', wary: 'óvatos', neutral: 'semleges', friendly: 'barátságos', devoted: 'elkötelezett' } as const;
const PROPOSAL_HU: Record<ProposalType, string> = { non_aggression: 'Megnemtámadási egyezmény', alliance: 'Szövetség', trade: 'Kereskedelem', peace: 'Béke' };

function SpeechBubble({ m, leader }: { m: NpcConversationMessage; leader: string }) {
  const tag = m.source === 'llm' ? 'AI által írt szöveg' : 'sablonszöveg';
  return (
    <div className="max-w-[92%] rounded-xl rounded-bl-sm border border-violet-400/30 bg-violet-500/10 px-3 py-2" data-testid="npc-speech" data-source={m.source}>
      <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-violet-300">
        <span>🗣 NPC beszéd · {leader}</span>
        <span className="rounded bg-violet-400/15 px-1.5 py-0.5 normal-case tracking-normal" title="A szöveg csak megjelenítés: a döntést a játékszabályok hozzák.">{tag}</span>
      </div>
      <p className="mt-1 text-sm text-slate-100">{m.text}</p>
    </div>
  );
}

function RulesLine({ m }: { m: NpcConversationMessage }) {
  const good = m.mechanical?.ok !== false;
  return (
    <div className={`mx-auto w-full rounded-lg border px-3 py-2 text-xs ${good ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-200' : 'border-amber-500/40 bg-amber-500/5 text-amber-200'}`} data-testid="rules-result">
      <span className="font-semibold uppercase tracking-wider text-[10px] opacity-80">⚙ Szabálymotor eredménye</span>
      <div className="mt-0.5 text-sm">{m.text}</div>
    </div>
  );
}

export function NpcConversationPanel({ rel, incoming, onClose, onChanged }: {
  rel: RelationView; incoming: ProposalView[]; onClose: () => void; onChanged: () => void;
}) {
  const [conv, setConv] = useState<NpcConversation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [confirmThreat, setConfirmThreat] = useState(false);
  const pushToast = useGameStore(s => s.pushToast);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    fetchNpcConversation(rel.civId).then(c => { setConv(c); setError(null); }).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
  }, [rel.civId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [conv?.messages.length]);

  const run = (body: NpcTalkBody) => {
    setBusy(true);
    talkToNpc(rel.civId, { ...body, ...(text.trim() ? { message: text.trim() } : {}) } as NpcTalkBody)
      .then(() => { setText(''); load(); onChanged(); })
      .catch(e => pushToast(e instanceof Error ? e.message : 'A művelet nem sikerült', 'error'))
      .finally(() => { setBusy(false); setConfirmThreat(false); });
  };

  const hostile = rel.status === 'hostile';
  const has = (t: string) => rel.treaties.some(x => x.type === t);
  const mine = incoming.filter(p => p.fromCivId === rel.civId);
  const btn = 'rounded-lg text-xs font-semibold px-3 min-h-[44px] border border-slate-600 text-slate-200 disabled:opacity-40';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`Tárgyalás: ${rel.civName}`} data-testid="npc-conversation"
        className="sci-fi-panel w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-4 space-y-3 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-100 truncate">{conv?.npc.leaderName ?? rel.civName}</h2>
            <p className="text-xs text-slate-400">
              {rel.civName}{conv ? ` · ${ARCHETYPE_LABEL[conv.npc.archetype]} · hozzáállása: ${ATTITUDE_LABEL[conv.npc.attitude]}` : ''}
            </p>
          </div>
          <button type="button" className="min-h-[44px] min-w-[44px] text-slate-400" aria-label="Bezárás" onClick={onClose}>✕</button>
        </div>
        <p className="text-[11px] text-slate-500">
          {conv?.llmActive ? 'Az NPC szavait nyelvi modell írhatja' : 'Az NPC szavai sablonokból állnak'} – de a szerződésekről, nyersanyagról és háborúról mindig a játékszabályok döntenek.
        </p>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[140px]" data-testid="npc-messages" aria-live="polite">
          {error && <p className="text-sm text-red-400">{error}</p>}
          {!conv && !error && <p className="text-sm text-slate-500">Betöltés…</p>}
          {conv?.messages.length === 0 && <p className="text-sm text-slate-500">Még nem beszéltetek. Válassz egy lépést lent.</p>}
          {conv?.messages.map(m => m.role === 'npc'
            ? <SpeechBubble key={m.id} m={m} leader={conv.npc.leaderName} />
            : m.role === 'system'
              ? <RulesLine key={m.id} m={m} />
              : <div key={m.id} className="ml-auto max-w-[85%] rounded-xl rounded-br-sm bg-cyan-500/10 border border-cyan-400/30 px-3 py-2 text-sm text-slate-100">{m.text}</div>)}
          <div ref={endRef} />
        </div>

        {mine.length > 0 && (
          <div className="rounded-lg border border-slate-600 p-2 space-y-2" data-testid="npc-incoming">
            {mine.map(p => (
              <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-200">
                <span className="flex-1">Javaslata: <b>{PROPOSAL_HU[p.type]}</b></span>
                <button type="button" className="btn-primary text-xs px-3 min-h-[44px]" disabled={busy} onClick={() => run({ intent: 'answer_proposal', proposalId: p.id, accept: true })}>Elfogad</button>
                <button type="button" className="btn-ghost text-xs px-3 min-h-[44px]" disabled={busy} onClick={() => run({ intent: 'answer_proposal', proposalId: p.id, accept: false })}>Elutasít</button>
              </div>
            ))}
          </div>
        )}

        <label className="block text-xs text-slate-400">Üzenet a vezetőnek (nem kötelező, legfeljebb {NPC_PLAYER_MESSAGE_MAX} karakter)
          <input type="text" value={text} maxLength={NPC_PLAYER_MESSAGE_MAX} onChange={e => setText(e.target.value)} data-testid="npc-message-input"
            className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-2 min-h-[44px] text-slate-100" placeholder="Pl.: Kössünk békét!" />
        </label>

        <div className="flex flex-wrap gap-2">
          {hostile ? (
            <button type="button" className={`${btn} border-emerald-500/50 text-emerald-300`} disabled={busy} onClick={() => run({ intent: 'propose', type: 'peace' })}>Békejavaslat</button>
          ) : (
            <>
              {!has('trade') && <button type="button" className={btn} disabled={busy} onClick={() => run({ intent: 'propose', type: 'trade' })}>Kereskedelem</button>}
              {!has('non_aggression') && !has('alliance') && <button type="button" className={btn} disabled={busy} onClick={() => run({ intent: 'propose', type: 'non_aggression' })}>Megnemtámadási egyezmény</button>}
              {!has('alliance') && <button type="button" className={btn} disabled={busy} onClick={() => run({ intent: 'propose', type: 'alliance' })}>Szövetség</button>}
              <button type="button" className={`${btn} border-amber-500/40 text-amber-300`} disabled={busy || Boolean(conv?.helpAvailableAt)}
                title={conv?.helpAvailableAt ? 'Újabb segítséget később kérhetsz' : undefined} onClick={() => run({ intent: 'ask_help', resource: 'metal' })}>Fém kérése</button>
              <button type="button" className={`${btn} border-amber-500/40 text-amber-300`} disabled={busy || Boolean(conv?.helpAvailableAt)} onClick={() => run({ intent: 'ask_help', resource: 'energy' })}>Energia kérése</button>
              <button type="button" className={`${btn} border-red-500/40 text-red-300`} disabled={busy} onClick={() => setConfirmThreat(true)}>Fenyegetés</button>
            </>
          )}
        </div>
        {confirmThreat && (
          <div className="rounded-lg border border-red-500/40 p-3 text-sm text-slate-200 space-y-2" role="alertdialog">
            <p>Megfenyegeted {rel.civName} vezetőjét. Haragtartó vezető háborút üzenhet, és a fenyegetés megmarad az emlékezetében.</p>
            <div className="flex gap-2">
              <button type="button" className="rounded-lg text-xs font-semibold px-3 min-h-[44px] border border-red-500/60 text-red-300" disabled={busy} onClick={() => run({ intent: 'threaten' })}>Megfenyegetem</button>
              <button type="button" className="btn-ghost text-xs px-3 min-h-[44px]" onClick={() => setConfirmThreat(false)}>Mégse</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
